import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Gauge } from 'lucide-react';
import { form_logic } from '../lib/data/form_logic';
import { motion } from 'framer-motion';
import { supabase } from '../lib/supabase';

interface PoliticalOffice {
  name: string;
  description: string;
  competitiveness: 'low' | 'medium' | 'high';
}

// Transform form_logic data into our office structure
const politicalOffices = Object.entries(form_logic).reduce((acc, [category, data]) => {
  const offices = Object.entries(data.seats).map(([name, seatData]) => ({
    name,
    description: seatData.synonyms.length > 0 
      ? `Also known as: ${seatData.synonyms.join(', ')}` 
      : seatData.required_fields.join(', '),
    competitiveness: seatData.competitiveness as 'low' | 'medium' | 'high'
  }));

  // Format category name for display
  const formattedCategory = category.replace(/([A-Z])/g, ' $1').trim();
  acc[formattedCategory] = offices;
  return acc;
}, {} as Record<string, PoliticalOffice[]>);

const competitivenessColors = {
  low: 'bg-green-500',
  medium: 'bg-yellow-500',
  high: 'bg-red-500'
};

export function Home() {
  const navigate = useNavigate();
  const [searchValue, setSearchValue] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [shakeError, setShakeError] = useState(false);
  const [placeholderColor, setPlaceholderColor] = useState('text-gray-400');
  const searchRef = useRef<HTMLDivElement>(null);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    handleGetUser()
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 640);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleGetUser = async () => {
     // Get current user
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      if (authError) {
        console.error('Auth error:', authError);
        // Continue without auth - we'll handle it in FormFlow
      }
      console.log('user====>',user)
         // If no user is logged in, create an anonymous user
        if (!user) {
          const { data: { user: _user }, error } = await supabase.auth.signInAnonymously();
          console.log('_user',_user)
          if (error) {
            console.error('Error creating anonymous user:', error);
          } else {
            const user = data.user; // This is the anonymous user
            alert(JSON.stringyfy(user));
          }
        }
  }

  const handleOfficeSelect = async (office: string) => {
    try {
      console.log('Selected office:', office);

      // Save selected office to localStorage for persistence
      localStorage.setItem('selectedOffice', office);

      // Update search value and hide suggestions
      setSearchValue(office);
      setShowSuggestions(false);

      // Navigate to form
      navigate('/form', { state: { office } });
    } catch (error) {
      console.error('Office selection error:', error);
      // Continue with navigation even if there's an error
      navigate('/form', { state: { office } });
    }
  };

  const handleGetStarted = async () => {
    if (!searchValue) {
      setShakeError(true);
      setPlaceholderColor('text-[#026fb9]');
      setTimeout(() => {
        setShakeError(false);
        setPlaceholderColor('text-gray-400');
      }, 1000);
      return;
    }

    try {
      await handleOfficeSelect(searchValue);
    } catch (error) {
      console.error('Get started error:', error);
      // Navigate anyway - we'll handle auth in FormFlow
      navigate('/form', { state: { office: searchValue } });
    }
  };

  const filteredOffices = Object.entries(politicalOffices).reduce((acc, [category, offices]) => {
    const filtered = offices.filter(office =>
      office.name.toLowerCase().includes(searchValue.toLowerCase()) ||
      office.description.toLowerCase().includes(searchValue.toLowerCase())
    );
    if (filtered.length > 0) {
      acc[category] = filtered;
    }
    return acc;
  }, {} as Record<string, PoliticalOffice[]>);

  // Get popular offices from form_logic
  const popularOffices = [
    'Mayor',
    'City Council Member',
    'School Board Member'
  ].map(office => {
    const category = Object.entries(form_logic).find(([_, data]) => 
      Object.keys(data.seats).includes(office)
    );
    if (category) {
      const seat = category[1].seats[office];
      return seat.synonyms[0] || office;
    }
    return office;
  });

  return (
    <main className="min-h-screen flex flex-col">
      <div className="flex-grow container mx-auto px-4 sm:px-6 lg:px-8 pt-8 sm:pt-32 pb-8 flex items-center justify-center">
        <div className="text-center space-y-6 sm:space-y-8 max-w-4xl mx-auto">
          <div className="text-base sm:text-lg md:text-xl text-[#026fb9] font-medium">
            Running For Office?
          </div>
          <h1 className="text-5xl sm:text-6xl md:text-6xl lg:text-7xl font-bold tracking-tight leading-tight sm:leading-normal">
            Unlock AI Insights for Every Seat
          </h1>
          <p className="text-base sm:text-xl text-gray-400 max-w-2xl mx-auto">
            Hyper-personalized campaign data for your strategic edge.
          </p>

          <div className="relative max-w-3xl mx-auto mt-6 sm:mt-16" ref={searchRef}>
            <motion.div
              animate={shakeError ? {
                x: [-10, 10, -10, 10, 0],
                transition: { duration: 0.4 }
              } : {}}
            >
              <input
                type="text"
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                onFocus={() => setShowSuggestions(true)}
                placeholder="Enter the Office You're Running For (e.g., Mayor)"
                className={`w-full px-4 sm:pl-12 pr-4 py-3 sm:py-4 bg-white/10 backdrop-blur-sm rounded-xl sm:rounded-full text-white placeholder:${placeholderColor} focus:outline-none focus:ring-2 focus:ring-[#026fb9] text-sm sm:text-base transition-all duration-200`}
              />
            </motion.div>

            <div className="mt-3 sm:mt-0 sm:absolute sm:right-2 sm:top-1/2 sm:-translate-y-1/2">
              <motion.button 
                onClick={handleGetStarted}
                animate={shakeError ? {
                  x: [-5, 5, -5, 5, 0],
                  transition: { duration: 0.4 }
                } : {}}
                className="w-full sm:w-auto px-4 sm:px-6 py-3 sm:py-2 bg-[#026fb9] text-white rounded-xl sm:rounded-full font-medium hover:bg-[#026fb9]/80 transition-all duration-300 flex items-center justify-center gap-2 group"
              >
                Get Started
                <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" />
              </motion.button>
            </div>

            {showSuggestions && searchValue && (
              <div className="absolute z-50 w-full mt-2 bg-[#1a2236] border border-white/10 rounded-xl shadow-xl backdrop-blur-lg max-h-[50vh] sm:max-h-[60vh] overflow-y-auto">
                {Object.entries(filteredOffices).map(([category, offices]) => (
                  <div key={category} className="border-b border-white/10 last:border-0">
                    <div className="px-3 sm:px-4 py-2 text-sm font-semibold text-gray-400 bg-white/5">
                      {category}
                    </div>
                    {offices.map((office) => (
                      <button
                        key={office.name}
                        className="w-full px-3 sm:px-4 py-3 text-left text-white hover:bg-white/10 transition-colors"
                        onClick={() => handleOfficeSelect(office.name)}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="font-medium">{office.name}</div>
                            <div className="text-sm text-gray-400">{office.description}</div>
                          </div>
                          <div className="flex items-center gap-2">
                            <div className="flex items-center gap-2 px-2 py-1 rounded bg-white/5">
                              <Gauge className="w-4 h-4" />
                              <div className={`w-12 sm:w-16 h-1.5 rounded-full ${competitivenessColors[office.competitiveness]}`} />
                            </div>
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="mt-6 sm:mt-12 text-gray-400 flex flex-wrap justify-center gap-3 sm:gap-6 text-sm sm:text-base">
            <span>Popular Offices:</span>
            {popularOffices.map((office) => (
              <button
                key={office}
                onClick={() => handleOfficeSelect(office)}
                className="hover:text-[#026fb9] transition-colors"
              >
                {office}
              </button>
            ))}
          </div>
        </div>
      </div>

      <footer className="py-4 sm:py-8 text-center border-t border-white/10">
        <div className="inline-flex items-center space-x-2">
          <img 
            src="https://storage.googleapis.com/msgsndr/cc3ljOrlhFxgAjJAxBN1/media/67c927f2cd9cc5eb568fedc6.png"
            alt="Campaign Finance Calculator Logo"
            className="h-5 w-auto sm:h-6"
          />
          <div className="text-sm sm:text-base">
            <span className="font-semibold">Campaign Finance Calculator</span>
            <span className="text-gray-400 text-xs sm:text-sm block">by Sutton & Smart</span>
          </div>
        </div>
      </footer>
    </main>
  );
}